import type { components } from '@sportik/api-coach/schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { activityKeyPrefix } from '@/services/activity';
import { api } from '@/services/api-client';

type Note = components['schemas']['Note'];
type NoteList = components['schemas']['NoteList'];

/** The one definition of this read: the coach's notes on a client, newest first. */
export function notesOptions(clientId: string) {
  return api.queryOptions('get', '/clients/{clientId}/notes', {
    params: { path: { clientId }, query: { limit: 200 } },
  });
}

/** The coach's notes on one client. */
export function useNotes(clientId: string) {
  return useQuery(notesOptions(clientId));
}

// The notes and the timeline that carries them are re-read whether the write
// landed or not: after a conflict what is on screen is the stale note.
function useNotesRefresh() {
  const queryClient = useQueryClient();
  return (
    _data: unknown,
    _error: unknown,
    { clientId }: { clientId: string },
  ) =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: notesOptions(clientId).queryKey,
      }),
      queryClient.invalidateQueries({ queryKey: activityKeyPrefix() }),
    ]);
}

type SaveNote = {
  clientId: string;
  /** A new note's id is made by the caller, so a retry is not a second note. */
  noteId: Note['id'];
  body: string;
  /** `0` writes a new note; an edit sends the version the coach last saw. */
  version: number;
};

/**
 * Writes a new note or saves an edit to one. A new note is listed at once
 * (ADR 0016 § 3) and taken back if the write fails; an edit waits for the API,
 * which may refuse the version.
 */
export function useSaveNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, noteId, body, version }: SaveNote) =>
      api.client.PUT('/clients/{clientId}/notes/{noteId}', {
        params: { path: { clientId, noteId } },
        body: { body, version },
      }),
    onMutate: async ({ clientId, noteId, body, version }) => {
      if (version !== 0) return undefined;
      const { queryKey } = notesOptions(clientId);
      // A read on its way would put the list back without the note.
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NoteList>(queryKey);
      const now = new Date().toISOString();
      queryClient.setQueryData<NoteList>(
        queryKey,
        (notes) =>
          notes && {
            ...notes,
            items: [
              { id: noteId, body, version: 1, createdAt: now, updatedAt: now },
              ...notes.items,
            ],
          },
      );
      return { previous };
    },
    onError: (_error, { clientId }, context) => {
      if (context) {
        queryClient.setQueryData(
          notesOptions(clientId).queryKey,
          context.previous,
        );
      }
    },
    onSettled: useNotesRefresh(),
  });
}

/** Takes a note off the client's notes and off the timeline. */
export function useDeleteNote() {
  return useMutation({
    mutationFn: ({ clientId, noteId }: Pick<SaveNote, 'clientId' | 'noteId'>) =>
      api.client.DELETE('/clients/{clientId}/notes/{noteId}', {
        params: { path: { clientId, noteId } },
      }),
    onSettled: useNotesRefresh(),
  });
}
